// -----------------------------------------------------------------------------
// src/features/transactions/transactionThunks.js
// -----------------------------------------------------------------------------
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * GET /api/transactions/?store_id=<storeId>&limit=<limit>&offset=<offset>
 * Supports DRF limit/offset pagination.
 * Pass an object { store_id, offset, limit } as the thunk arg.
 * For backward compatibility, also accepts storeId parameter.
 */
export const fetchTransactionsThunk = createAsyncThunk(
  'transactions/fetchAll',
  async ({ store_id = null, storeId = null, offset = 0, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      
      // Use store_id if provided, fall back to storeId for backward compatibility
      const effectiveStoreId = store_id || storeId;
      if (effectiveStoreId) params.append('store_id', effectiveStoreId);
      
      const response = await axiosInstance.get(`/api/transactions/?${params.toString()}`);
      return response.data; // { count, next, previous, results }
    } catch (error) {
      return rejectWithValue('Failed to fetch transactions');
    }
  }
);

/**
 * GET /api/transactions/<transaction_id>/detail/
 * Fetches a single transaction by ID
 */
export const fetchSingleTransactionThunk = createAsyncThunk(
  'transactions/fetchSingle',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/transactions/${transactionId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(`Failed to fetch transaction: ${error.message}`);
    }
  }
);

/**
 * PATCH /api/transactions/<transaction_id>/
 */
export const updateTransactionThunk = createAsyncThunk(
  'transactions/update',
  async ({ transactionId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/transactions/${transactionId}/`, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to update transaction');
    }
  }
);